import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useRouter } from "next/router";
import urlJoin from "proper-url-join";
import PreviewMode from "components/preview-mode";
import { getClient } from "lib/sanity.server";
import { usePreviewSubscription } from "lib/sanity";
import HeadSEO from "components/head-seo";
import { QuizModule } from "components/quiz-module";
import { QUESTION_TYPES, transformSanityQuestion } from "utils/quizUtils";
import { GET_ALL_QUIZ_SLUGS_QUERY, GET_QUIZ_DATA_BY_SLUG_QUERY } from "../../data";
import segmentUtils from "utils/segmentUtils";
import { getPageLink } from "components/link";
import useUser from "hooks/useUser";
import useGeolocationInfo from "hooks/useGeolocationInfo";

const QuizPage = ({ data: initialData, slug, preview }) => {
  const {
    error,
    loading,
    data: { quiz, site },
  } = usePreviewSubscription(GET_QUIZ_DATA_BY_SLUG_QUERY, {
    params: { slug: slug[0] },
    initialData,
    enabled: preview,
  });

  const router = useRouter();
  const [position, setPosition] = useState(slug[1]);
  const [questions, setQuestions] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const question = position > 0 ? questions?.[position - 1] : null;
  const { user } = useUser();
  const { geoLocationInfo, isLoading: geolocationInfoLoading } = useGeolocationInfo();

  const analyticsEventCommonOptions = useMemo(
    () =>
      geolocationInfoLoading
        ? null
        : {
            context: {
              location: geoLocationInfo?.location,
              ip: geoLocationInfo?.ip,
            },
          },
    [geoLocationInfo, geolocationInfoLoading]
  );

  useEffect(() => {
    if (!quiz?.questions?.length) {
      return;
    }

    setQuestions(quiz?.questions?.map(transformSanityQuestion));
  }, [quiz?.questions]);

  useEffect(() => {
    if (slug[0] && !slug[1]) {
      setPosition(1);
    }
  }, [slug[0], slug[1]]);

  const onClose = useCallback(() => router.push("/"), []);

  const webHookQuestion = useMemo(() => {
    if (!questions?.length) {
      return null;
    }
    return questions.find((question) => !!question.webhookURL);
  }, [questions]);

  const handleWebhook = useCallback(
    async (questionsUpdated) => {
      if (!webHookQuestion) {
        return;
      }
      const payload = [];
      questionsUpdated.forEach((question) => {
        const questionInput = {
          tableName: question.linkedTable?.tableName,
          type: question.type,
          question,
          input: {},
        };
        if (question.type === QUESTION_TYPES.standard) {
          questionInput.input = {
            [question.linkedTable?.columnName]: question.allowMultiAnswers ? question.answer : question.answer?.[0],
          };
        } else {
          questionInput.input = {
            [question.linkedTable?.columnName]: question.answer,
          };
        }

        payload.push(questionInput);
      });
      await fetch(webHookQuestion.webhookURL, {
        method: "POST",
        body: JSON.stringify(payload),
      });
    },
    [webHookQuestion, questions]
  );

  const handleNext = async (questionKey, answer) => {
    const questionsUpdated = questions.map((questionItem) => {
      if (questionItem._key === questionKey) {
        return {
          ...questionItem,
          answer,
        };
      }
      return questionItem;
    });
    const updatedQuestion = questionsUpdated.find((question) => question._key === questionKey);

    setQuestions(questionsUpdated);

    if (position === 1) {
      segmentUtils.sendQuizStartedEvent("1", slug[0], user, analyticsEventCommonOptions);
    }
    if (question?._type === "quizQuestionImage") {
      segmentUtils.sendQuizStyleImagesSubmitEvent(question, answer, user, analyticsEventCommonOptions);
    } else {
      segmentUtils.sendQuizAnswerSubmitEvent({
        quizQuestion: question,
        currentAnswers: answer,
        user,
        options: analyticsEventCommonOptions,
      });
    }

    window?.scrollTo(0, 0);
    const finalPosition = position + 1;
    if (finalPosition > questionsUpdated.length) {
      setIsSubmitting(true);
      // Quiz ended
      if (webHookQuestion) {
        await handleWebhook(questionsUpdated);
      }
      segmentUtils.sendQuizEndedEvent(slug[0], router.pathname, questionsUpdated, user, analyticsEventCommonOptions);
      if (updatedQuestion?.redirectLink) {
        // Redirect if set
        const to = getPageLink(updatedQuestion?.redirectLink);
        if (to) {
          setIsSubmitting(false);
          await router.push(to.startsWith("/") || to.startsWith("http") ? to : `/${to}`);
        }
      }
      setIsSubmitting(false);
      return;
    }
    await router.push(`/quiz/${slug[0]}/${finalPosition}`, "", {
      shallow: true,
    });
    setPosition(finalPosition);
  };

  const handlePrevious = useCallback(() => {
    router.back();
    setPosition(position - 1);
  }, [position]);

  if (loading) {
    return <h4>loading...</h4>;
  }

  if (error) {
    return <h4>Error: ${error}</h4>;
  }

  return (
    <>
      {preview && <PreviewMode />}
      <HeadSEO page={{ seo: quiz.seo, site }} />
      {question && (
        <QuizModule
          slug={slug}
          onClose={onClose}
          question={question}
          onNext={handleNext}
          onBack={handlePrevious}
          allQuestions={questions}
          currentQuestion={position}
          numberQuestions={questions?.length}
          isSubmitting={isSubmitting}
        />
      )}
    </>
  );
};

export const getStaticPaths = async () => {
  const { slugs } = await getClient().fetch(GET_ALL_QUIZ_SLUGS_QUERY);
  const paths = slugs.map((slug) => ({
    params: {
      slug: [urlJoin(slug, { leadingSlash: false })].filter(Boolean),
    },
  }));

  return { paths, fallback: "blocking" };
};

export const getStaticProps = async ({ params, preview = false }) => {
  const { slug } = params;
  const data = await getClient(preview).fetch(GET_QUIZ_DATA_BY_SLUG_QUERY, { slug: slug[0] });
  if (!data?.quiz) {
    return { notFound: true, revalidate: 60 };
  }
  return {
    props: { data, slug, preview },
    revalidate: 10,
  };
};

export default QuizPage;
